import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Loader2, Lock } from 'lucide-react';

export default function LoginPage() {
  const [pin, setPin] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);
    setError('');

    if (value && index < 3) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 4 digits entered
    if (value && index === 3 && newPin.every(d => d !== '')) {
      handleLogin(newPin.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 4);
    if (pasted.length === 4) {
      const newPin = pasted.split('');
      setPin(newPin);
      inputRefs[3].current?.focus();
      handleLogin(pasted);
    }
  };

  const handleLogin = async (pinCode) => {
    setLoading(true);
    setError('');

    const result = await login(null, null, pinCode);
    setLoading(false);

    if (result.success) {
      const role = result.data.role;
      if (role === 'admin') navigate('/admin');
      else if (role === 'cajero') navigate('/pos');
      else if (role === 'operador') navigate('/agenda');
      else navigate('/');
    } else {
      setError('PIN incorrecto');
      setPin(['', '', '', '']);
      inputRefs[0].current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const pinCode = pin.join('');
    if (pinCode.length === 4) {
      handleLogin(pinCode);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <div className="p-6">
        <div className="flex items-center gap-3">
          <img
            src="https://customer-assets.emergentagent.com/job_carwash-quick-pos/artifacts/tady4vql_image.png"
            alt="WD Logo"
            className="w-12 h-12 object-contain"
          />
          <span className="text-2xl font-bold text-white">WD Lavado Automotriz</span>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-sm">
          <div className="card-base p-8 animate-fadeIn">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Acceso</h1>
              <p className="text-slate-500 mt-2">Ingresa tu PIN de 4 digitos</p>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-100 border border-red-200 text-red-700 rounded-lg text-sm text-center font-medium" data-testid="login-error">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="flex justify-center gap-4 mb-8" data-testid="pin-inputs">
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={inputRefs[index]}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className="w-16 h-20 text-center text-3xl font-bold border-2 border-slate-200 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition-all bg-slate-50 text-slate-900"
                    data-testid={`pin-digit-${index}`}
                  />
                ))}
              </div>

              <Button
                type="submit"
                className="w-full h-14 text-lg"
                disabled={loading || pin.some(d => d === '')}
                data-testid="staff-login-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Entrar'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
